const DEFAULT_API_ORIGIN = 'http://127.0.0.1:8000';

/** Strip trailing slashes and a trailing /api segment so BASE is never /api/api/... */
export function normalizeApiOrigin(raw) {
  const trimmed = String(raw || DEFAULT_API_ORIGIN).trim().replace(/\/+$/, '');
  return trimmed.replace(/\/api$/i, '') || DEFAULT_API_ORIGIN;
}

export const endPoint = normalizeApiOrigin(process.env.REACT_APP_API_ENDPOINT);
export const apiBase = `${endPoint}/api/`;
const BASE = apiBase;

const API = {
  // Auth
  REGISTER:            `${BASE}register/`,
  LOGIN:               `${BASE}login/`,
  PROFILE:             `${BASE}profile/`,
  PROFILE_UPDATE:      `${BASE}profile/update/`,
  TOKEN_REFRESH:       `${BASE}token/refresh/`,
  LOGOUT:              `${BASE}logout/`,

  // Email
  VERIFY_EMAIL:        `${BASE}verify-email/`,
  RESEND_VERIFICATION: `${BASE}resend-verification/`,

  // Password
  FORGOT_PASSWORD:     `${BASE}forgot-password/`,
  RESET_PASSWORD:      `${BASE}reset-password/`,
  CHANGE_PASSWORD:     `${BASE}change-password/`,

  // Membership (legacy reads — prefer CURRENT_PLAN for plan state)
  MEMBERSHIP:          `${BASE}membership/`,
  PAYMENT_HISTORY:     `${BASE}membership/payments/`,
  CURRENT_PLAN:        `${BASE}current-plan/`,
  PLANS_HISTORY:       `${BASE}v2/plans/history/`,
  UPLOAD_PLAN_SCREENSHOT: `${BASE}v2/plans/upload-screenshot/`,
  PLAN_BY_TYPE: (type) => `${BASE}v2/plans/plan-by-type/${type}/`,

  // Earnings
  TRANSACTIONS:        `${BASE}transactions/`,
  EARNINGS_SUMMARY:    `${BASE}earnings/summary/`,

  // Referrals
  GET_REFERRALS:       `${BASE}referrals/`,
  GET_LEADERBOARD:     `${BASE}leaderboard/`,
  LEVEL_TIERS:         `${BASE}v2/network/level-tiers/`,

  // Withdrawals (member: history + acknowledge; admin monthly payout management)
  MY_WITHDRAWALS:      `${BASE}withdrawals/my/`,
  WITHDRAWAL_SUMMARY:  `${BASE}withdrawals/summary/`,
  WITHDRAWAL_ACCEPT:   (id) => `${BASE}withdrawals/${id}/accept/`,
  WITHDRAWAL_RAISE_CONCERN: (id) => `${BASE}withdrawals/${id}/raise-concern/`,
  WITHDRAWAL_REOPEN_CONCERN: (id) => `${BASE}withdrawals/concerns/${id}/reopen/`,

  // Payment retry (after admin rejection)
  PAYMENT_RETRY_CONTEXT: `${BASE}v2/payments/retry/context/`,
  PAYMENT_RETRY_SUBMIT:  `${BASE}v2/payments/retry/submit/`,
  PAYMENT_ATTEMPT_CREATE: `${BASE}v2/payments/attempts/`,
  PAYMENT_ATTEMPT_EVENT:  (sessionId) => `${BASE}v2/payments/attempts/${sessionId}/events/`,

  // Account recovery (blocked members)
  RECOVERY_CONTEXT:      `${BASE}v2/recovery/context/`,
  RECOVERY_SUBMIT:       `${BASE}v2/recovery/submit/`,
  ADMIN_RECOVERY_PREVIEW: `${BASE}v2/admin/recovery/preview/`,
  ADMIN_RECOVERY_GENERATE: `${BASE}v2/admin/recovery/generate-link/`,
  ADMIN_RECOVERY_LIST:   `${BASE}v2/admin/recovery/`,
  ADMIN_RECOVERY_DETAIL: (id) => `${BASE}v2/admin/recovery/${id}/`,
  ADMIN_RECOVERY_APPROVE: (id) => `${BASE}v2/admin/recovery/${id}/approve/`,
  ADMIN_RECOVERY_REJECT: (id) => `${BASE}v2/admin/recovery/${id}/reject/`,

  // Community
  COMMUNITY:           `${BASE}community/`,
  COMMUNITY_CREATE:    `${BASE}community/create/`,
  COMMUNITY_POST:      (pk) => `${BASE}community/${pk}/`,

  // Admin
  ADMIN_STATS:         `${BASE}admin/stats/`,
  ADMIN_USERS:         `${BASE}admin/users/`,
  ADMIN_USERS_V2:      `${BASE}v2/admin/users/`,
  ADMIN_USER_V2:       (id) => `${BASE}v2/admin/users/${id}/`,
  ADMIN_USER_ARCHIVE:  (id) => `${BASE}v2/admin/users/${id}/archive/`,
  ADMIN_USER_RESTORE:  (id) => `${BASE}v2/admin/users/${id}/restore/`,
  ADMIN_USER_RESET_PASSWORD: (id) => `${BASE}v2/admin/users/${id}/reset-password/`,
  ADMIN_BONUS:         (id) => `${BASE}admin/users/${id}/bonus/`,
  ADMIN_BLOCK:         (id) => `${BASE}admin/users/${id}/block/`,
  ADMIN_PAYMENT_UPLOAD: (id) => `${BASE}admin/users/${id}/payment-upload/`,
  ADMIN_BANK_DETAILS_EDIT: (id) => `${BASE}admin/users/${id}/bank-details-edit/`,
  ADMIN_WITHDRAWALS:   `${BASE}admin/withdrawals/`,
  ADMIN_WITHDRAWAL:    (id) => `${BASE}admin/withdrawals/${id}/`,
  ADMIN_WITHDRAWAL_APPROVE: (id) => `${BASE}admin/withdrawals/${id}/approve/`,
  ADMIN_WITHDRAWAL_REJECT: (id) => `${BASE}admin/withdrawals/${id}/reject/`,
  ADMIN_WITHDRAWAL_MARK_PAID: (id) => `${BASE}admin/withdrawals/${id}/mark-paid/`,
  ADMIN_WITHDRAWAL_FINALIZE_CYCLE: `${BASE}admin/withdrawals/finalize-cycle/`,
  ADMIN_WITHDRAWAL_CONCERNS: `${BASE}admin/withdrawals/concerns/`,
  ADMIN_WITHDRAWAL_CONCERN_ACTION: (id) => `${BASE}admin/withdrawals/concerns/${id}/action/`,
  ADMIN_WITHDRAWAL_RECALCULATE: (id) => `${BASE}admin/withdrawals/${id}/recalculate/`,
  ADMIN_WITHDRAWAL_USER_RECALCULATE: (userId) => `${BASE}admin/withdrawals/user/${userId}/recalculate/`,
  ADMIN_WITHDRAWAL_REVIEW_CONCERN: (id) => `${BASE}admin/withdrawals/${id}/review-concern/`,
  ADMIN_WITHDRAWAL_RESOLVE_CONCERN: (id) => `${BASE}admin/withdrawals/${id}/resolve-concern/`,
  ADMIN_WITHDRAWAL_REVERT: (id) => `${BASE}admin/withdrawals/${id}/revert/`,
  ADMIN_USER_PAYMENTS:     `${BASE}v2/admin/payments/pending/`,
  ADMIN_PAYMENTS_HISTORY:  `${BASE}v2/admin/payments/history/`,
  ADMIN_PAYMENT_APPROVE:   (id) => `${BASE}v2/admin/payment/${id}/approve/`,
  ADMIN_PAYMENT_REJECT:    (id) => `${BASE}v2/admin/payment/${id}/reject/`,
  ADMIN_PAYMENT_REVERT:    (id) => `${BASE}v2/admin/payment/${id}/revert/`,
  ADMIN_PAYMENT_AUDIT:     (id) => `${BASE}v2/admin/payment/${id}/audit/`,
  ADMIN_PAYMENT_HISTORY:   `${BASE}v2/admin/payments/audit/`,
  ADMIN_REFERRAL_HISTORY:  `${BASE}v2/admin/referral-history/`,
  ADMIN_REFERRAL_HISTORY_DETAIL: (id) => `${BASE}v2/admin/referral-history/${id}/`,
  ADMIN_REFERRAL_CLAIMS:   `${BASE}v2/admin/referral-claims/`,
  ADMIN_REFERRAL_CLAIM_APPROVE: (id) => `${BASE}v2/admin/referral-claims/${id}/approve/`,
  ADMIN_REFERRAL_CLAIM_REJECT: (id) => `${BASE}v2/admin/referral-claims/${id}/reject/`,
  ADMIN_MLM_COMMISSION_LOG: `${BASE}v2/admin/mlm-commission-log/`,
  ADMIN_MLM_ACTIVE_LEDGER: `${BASE}v2/admin/mlm-commission-log/active/`,
  REFERRAL_CLAIM_STATUS:   `${BASE}v2/referral-claim/status/`,
  REFERRAL_CLAIM_SUBMIT:   `${BASE}v2/referral-claim/`,
  /** Admin product catalog */
  ADMIN_PRODUCTS:      `${BASE}admin/products/`,
  ADMIN_PRODUCT:       (id) => `${BASE}admin/products/${id}/`,
  PRODUCT:             (id) => `${BASE}products/${id}/`,
  PRODUCTS_CATALOG:    `${BASE}products/catalog/`,
};

export default API;
